import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Image,
  Center,
} from '@chakra-ui/react'
import {useEffect, useState, useMemo, useCallback} from 'react'
import {ReactComponent as ArrowRightIcon} from '@assets/svgs/learn/arrow_right_6.svg'
import {CommonColors} from '@utils/CommonStyles'
import avatarIntro from '@assets/images/paraphrase/avatarIntro.png'
import {
  updateCurrentSentence,
  updateIsCorrectGrammars,
  updateSessionId,
  updateType,
} from '@redux/paraphrase/paraphraseSlice'
import {useDispatch, useSelector} from 'react-redux'
import {paraphraseRequest} from '@apis/paraphrase'
import Loading from '@components/Loading'
import GlobalButton from '@components/GlobalButton/GlobalButton'
import {ParaphraseType} from './index'

// Add proper types
interface Topic {
  id: number
  title_en: string
  title_vi: string
}

interface Sentence {
  text_en: string
  text_vi: string
}

interface TopicData {
  topics: Topic[]
  current_page: number
  total_pages: number
}

interface SentencesData {
  user_sentences: Sentence[]
  sample_sentences: Sentence[]
}

const PickSentence = () => {
  const {currentSentence} = useSelector((state: any) => state.paraphrase)
  const [inputValue, setInputValue] = useState('')
  const [currentTopic, setCurrentTopic] = useState<Topic | null>(null)
  const [topicData, setTopicData] = useState<TopicData>({
    topics: [],
    current_page: 1,
    total_pages: 1,
  })
  const [page, setPage] = useState(1)
  const [sentences, setSentences] = useState<SentencesData>({
    user_sentences: [],
    sample_sentences: [],
  })
  const [isLoadingSentences, setIsLoadingSentences] = useState(false)
  const [isLoadingTopics, setIsLoadingTopics] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    const initSession = async () => {
      const res = await paraphraseRequest.initSession()
      dispatch(updateSessionId(res?.data?.data?.session_id))
    }
    initSession()
  }, [])

  // Memoize sentence validation
  const isInvalidSentenceMemo = useMemo(() => {
    if (!inputValue) return true
    const wordCount = inputValue.trim().split(/\s+/).length
    const hasTextAfterPeriod = /\..+/.test(inputValue.trim())
    return wordCount < 3 || hasTextAfterPeriod
  }, [inputValue])

  // Improve error handling in API calls
  const getTopics = useCallback(async () => {
    try {
      setIsLoadingTopics(true)
      const res = await paraphraseRequest.getTopics({page})
      const {topics = []} = res?.data?.data || {}
      setTopicData(prev => ({
        ...res?.data?.data,
        topics: [...prev.topics, ...topics],
      }))
    } catch (error) {
      console.error('Failed to fetch topics:', error)
    } finally {
      setIsLoadingTopics(false)
    }
  }, [page])

  const getSentences = useCallback(async (topicId: number) => {
    try {
      setIsLoadingSentences(true)
      const res = await paraphraseRequest.getSentences({topicId})
      setSentences(
        res?.data?.data || {
          user_sentences: [],
          sample_sentences: [],
        },
      )
    } catch (error) {
      console.error('Failed to fetch sentences:', error)
      setSentences({
        user_sentences: [],
        sample_sentences: [],
      })
    } finally {
      setIsLoadingSentences(false)
    }
  }, [])

  useEffect(() => {
    getTopics()
  }, [getTopics])

  useEffect(() => {
    if (currentTopic?.id) {
      getSentences(currentTopic.id)
    }
  }, [currentTopic, getSentences])

  const handleContinue = () => {
    dispatch(updateType(ParaphraseType.SHOW_SENTENCE))
    dispatch(updateCurrentSentence({text_en: inputValue}))
    dispatch(updateIsCorrectGrammars(true))
  }

  const handleClose = () => {
    setCurrentTopic(null)
    dispatch(updateCurrentSentence(null))
  }

  return (
    <Flex flexDirection={'column'} h={'100%'}>
      <Box bg={'#fff'} p={{base: '16px', md: '32px'}} borderRadius={'24px'}>
        <Text
          fontSize={{base: '20px', md: '24px'}}
          fontWeight={'600'}
          textAlign={'center'}>
          Câu của bạn
        </Text>
        <Flex
          mt={{base: '16px', md: '32px'}}
          alignItems={'center'}
          border={'1px solid #EFEFEF'}
          borderRadius={{base: '20px', md: '99px'}}
          p={{base: '12px', md: '16px'}}
          gap={{base: '16px', md: '32px'}}
          flexDirection={{base: 'column', md: 'row'}}>
          <Input
            color={isInvalidSentenceMemo ? 'red' : ''}
            placeholder="Nhập một câu tiếng Anh bất kỳ"
            border={'none'}
            onChange={e => setInputValue(e.target.value)}
          />
          <GlobalButton
            h={'48px'}
            w={{base: '100%', md: '130px'}}
            isDisabled={isInvalidSentenceMemo}
            borderRadius={'99px'}
            rightIcon={<Icon as={ArrowRightIcon} w={'20px'} h={'20px'} />}
            onClick={handleContinue}>
            Tiếp tục
          </GlobalButton>
        </Flex>
        {inputValue && isInvalidSentenceMemo && (
          <Text color={'red'} fontSize={'12px'} mt={'16px'} ml={'32px'}>
            x Câu không hợp lệ
          </Text>
        )}
        <Text
          p={'16px'}
          px={'32px'}
          bg={'#EEF8FF'}
          fontSize={'14px'}
          mt={'16px'}
          borderRadius={{base: '20px', md: '99px'}}>
          Lưu ý: Hiện tại công cụ chỉ hỗ trợ paraphrase{' '}
          <Text as={'span'} fontWeight={'600'}>
            1 câu chứa 8 đến 25 từ kết thúc bằng dấu chấm.
          </Text>{' '}
          Bạn chú ý nhập đúng điều kiện để đạt được kết quả tốt nhất nhé!
        </Text>
      </Box>
      <Box
        mt={'32px'}
        bg={'#fff'}
        p={{base: '16px', md: '32px'}}
        borderRadius={'24px'}
        flex={1}
        overflow={'auto'}>
        <Text
          fontSize={{base: '20px', md: '24px'}}
          fontWeight={'600'}
          textAlign={'center'}>
          Hoặc chọn câu theo chủ đề
        </Text>
        {isLoadingTopics && <Loading />}
        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          gap={{base: '16px', md: '24px'}}
          mt={{base: '16px', md: '32px'}}>
          {topicData?.topics?.map((item: any, index: any) => (
            <Flex
              key={index}
              p={'20px'}
              gap={'16px'}
              borderRadius={'12px'}
              border={'1px solid #EFEFEF'}
              justifyContent={'space-between'}
              alignItems={'center'}
              cursor={'pointer'}
              _hover={{
                boxShadow: '0px 8px 0px 0px var(--Dodger-Blue-300, #8ED4FF)',
                border: '1px solid var(--Dodger-Blue-300, #8ED4FF)',
              }}
              onClick={() => setCurrentTopic(item)}>
              <Box>
                <Text fontSize={'18px'} fontWeight={'600'}>
                  {item.title_en}
                </Text>
                <Text fontSize={'14px'}>{item.title_vi}</Text>
              </Box>
              <Icon
                as={ArrowRightIcon}
                w={'20px'}
                h={'20px'}
                sx={{path: {stroke: '#BDBDBD'}}}
              />
            </Flex>
          ))}
        </Grid>
        {topicData?.current_page < topicData?.total_pages && (
          <Center mt={'24px'}>
            <Text
              onClick={() => setPage(page + 1)}
              textDecoration={'underline'}
              cursor={'pointer'}
              color={CommonColors.primary}
              fontSize={'16px'}
              fontWeight={'600'}>
              Xem thêm
            </Text>
          </Center>
        )}
      </Box>

      <Modal
        isOpen={!!currentTopic}
        onClose={handleClose}
        isCentered
        closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent
          maxW={{base: '95%', md: '1200px'}}
          mx={{base: '16px', md: 'auto'}}>
          {isLoadingSentences && <Loading />}
          <ModalHeader pb={0}>
            <Flex
              alignItems={'center'}
              gap={'16px'}
              flexDirection={{base: 'column', md: 'row'}}>
              <Image
                src={avatarIntro}
                w={{base: '100px', md: '140px'}}
                alt={currentTopic?.title_en}
              />
              <Box>
                <Text fontSize={{base: '24px', md: '30px'}} fontWeight={'600'}>
                  {currentTopic?.title_en}
                </Text>
                <Text fontSize={'12px'} color={'gray.600'} mt={'8px'}>
                  {currentTopic?.title_vi}
                </Text>
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton size="lg" />
          <ModalBody pt={0} pb={6}>
            <Box
              border={'1px solid #D6EBFF'}
              borderRadius={'10px'}
              p={'20px'}
              maxH={'500px'}
              overflow={'auto'}>
              <RenderSentences
                title={'Câu của bạn'}
                data={sentences?.user_sentences || []}
                currentSentence={currentSentence}
              />
              <RenderSentences
                title={'Câu mẫu'}
                data={sentences?.sample_sentences || []}
                style={{mt: '24px'}}
                currentSentence={currentSentence}
              />
            </Box>
            <Center mt={'24px'}>
              <Button
                isDisabled={!currentSentence}
                h={'48px'}
                borderRadius={'99px'}
                bg={CommonColors.primary}
                color={'#fff'}
                rightIcon={<Icon as={ArrowRightIcon} w={'20px'} h={'20px'} />}
                _hover={{
                  bg: CommonColors.primary,
                }}
                onClick={() => {
                  dispatch(updateType(ParaphraseType.SHOW_SENTENCE))
                  dispatch(updateIsCorrectGrammars(false))
                }}>
                Tiếp tục
              </Button>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  )
}

// Separate RenderSentences into its own component file
interface RenderSentencesProps {
  title: string
  data: Sentence[]
  style?: Record<string, any>
  currentSentence: Sentence | null
}

const RenderSentences: React.FC<RenderSentencesProps> = ({
  title,
  data,
  style,
  currentSentence,
}) => {
  const dispatch = useDispatch()

  return (
    <Box {...style}>
      <Text
        fontSize={{base: '16px', md: '18px'}}
        fontWeight={'600'}
        textTransform={'uppercase'}
        color={CommonColors.primary}>
        {title}
      </Text>
      <Flex flexDirection={'column'} mt={{base: '12px', md: '16px'}}>
        {data.length > 0 ? (
          data.map((item: any, index: any) => (
            <Flex
              key={index}
              flexDirection={'column'}
              gap={'4px'}
              borderRadius={'16px'}
              p={'16px'}
              cursor={'pointer'}
              bg={
                currentSentence?.text_en === item.text_en
                  ? '#E6F7FF'
                  : 'transparent'
              }
              _hover={{bg: '#E6F7FF'}}
              onClick={() => dispatch(updateCurrentSentence(item))}>
              <Text fontSize={'16px'} fontWeight={'600'} color={'#1F1F1F'}>
                {item.text_en}
              </Text>
              <Text fontSize={'12px'} fontWeight={'600'} color={'#8C8C8C'}>
                {item.text_vi}
              </Text>
            </Flex>
          ))
        ) : (
          <Text
            fontSize={'14px'}
            color={'gray.500'}
            textAlign={'center'}
            py={4}>
            Không có câu nào trong danh mục này
          </Text>
        )}
      </Flex>
    </Box>
  )
}

export default PickSentence
